import { useState, useEffect } from 'react';
import { X, Save, CheckSquare, Square } from 'lucide-react';
import { colors } from '../../styles/colors';

/**
 * Modal de configuration des consultants actifs
 *
 * @param {boolean} show - Afficher le modal
 * @param {Function} onClose - Callback de fermeture
 * @param {Function} onSave - Callback de sauvegarde (annee, consultantIds[])
 * @param {string[]} selectedConsultantIds - IDs des consultants actuellement sélectionnés
 * @param {Array<{id: string, nom: string}>} consultantsList - Liste de tous les consultants
 * @param {number} annee - Année concernée
 */
function ConsultantConfigModal({ show, onClose, onSave, selectedConsultantIds = [], consultantsList = [], annee }) {
  const [selected, setSelected] = useState(new Set(selectedConsultantIds));
  const [submitting, setSubmitting] = useState(false);

  // Synchroniser quand les props changent (changement d'année)
  useEffect(() => {
    if (show) {
      setSelected(new Set(selectedConsultantIds));
    }
  }, [show, selectedConsultantIds]);

  if (!show) return null;

  const sortedConsultants = [...consultantsList].sort((a, b) => a.nom.localeCompare(b.nom));
  const allSelected = sortedConsultants.length > 0 && selected.size === sortedConsultants.length;

  const toggleConsultant = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sortedConsultants.map(c => c.id)));
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSave(annee, Array.from(selected));
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      fontFamily: '"Noto Sans", sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.25rem 1.5rem',
          borderBottom: `1px solid ${colors.secondary.lightGray}`,
          flexShrink: 0
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary.gray, margin: 0 }}>
            Consultants actifs
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: colors.secondary.mediumGray,
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Description */}
        <div style={{
          padding: '1rem 1.5rem 0.5rem',
          flexShrink: 0
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.secondary.mediumGray,
            margin: 0
          }}>
            Sélectionnez les consultants actifs pour l'année <strong style={{ color: colors.primary.teal }}>{annee}</strong>.
            Les charges partagées seront divisées entre les consultants sélectionnés.
          </p>
        </div>

        {/* Bouton Tout sélectionner */}
        <div style={{
          padding: '0.75rem 1.5rem',
          borderBottom: `1px solid ${colors.secondary.lightGray}`,
          flexShrink: 0
        }}>
          <button
            onClick={toggleAll}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem 0',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: colors.primary.teal,
              fontFamily: '"Noto Sans", sans-serif'
            }}
          >
            {allSelected ? <CheckSquare size={18} /> : <Square size={18} />}
            {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
          </button>
        </div>

        {/* Liste des consultants */}
        <div style={{
          padding: '0.5rem 1rem',
          overflowY: 'auto',
          flex: 1
        }}>
          {sortedConsultants.length === 0 ? (
            <p style={{
              textAlign: 'center',
              color: colors.secondary.mediumGray,
              fontSize: '0.875rem',
              padding: '1.5rem 0'
            }}>
              Aucun consultant disponible
            </p>
          ) : (
            sortedConsultants.map(consultant => {
              const isChecked = selected.has(consultant.id);
              return (
                <label
                  key={consultant.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    transition: 'background-color 150ms'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.secondary.lightGray}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleConsultant(consultant.id)}
                    style={{
                      width: '1rem',
                      height: '1rem',
                      accentColor: colors.primary.teal,
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{
                    fontSize: '0.9375rem',
                    color: colors.primary.gray,
                    fontWeight: isChecked ? '600' : '400'
                  }}>
                    {consultant.nom}
                  </span>
                </label>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: `1px solid ${colors.secondary.lightGray}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <span style={{
            fontSize: '0.8125rem',
            color: colors.secondary.mediumGray
          }}>
            {selected.size} consultant{selected.size !== 1 ? 's' : ''} sélectionné{selected.size !== 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: colors.secondary.mediumGray,
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontFamily: '"Noto Sans", sans-serif'
              }}
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: colors.primary.teal,
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                opacity: submitting ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontFamily: '"Noto Sans", sans-serif'
              }}
            >
              <Save size={16} />
              {submitting ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConsultantConfigModal;
